import { apiFetch } from '../../lib/api-client';
import type {
    ComputeFilterOptions,
    ComputeFilters,
    ComputeInsightKind,
    ComputeInsightResponse,
    ComputeSeries,
    ComputeSeriesPoint,
    ComputeSourceStatusResponse,
} from './computeTypes';

const COMPUTE_API_ROOT = '/api/service/SPO/compute';
const RESPONSE_CACHE_TTL_MS = 5 * 60 * 1000;
const RESPONSE_CACHE_MAX_ENTRIES = 50;

type CacheEntry = { expiresAt: number; payload: Promise<unknown> };

// Short-lived cache of report responses so switching tabs or returning to earlier filters is instant.
// Requests for the same URL share one network call; failures are never cached.
const responseCache = new Map<string, CacheEntry>();

export function clearComputeResponseCache(): void {
    responseCache.clear();
}

// The site serves its HTML page for unknown paths, so check the payload is JSON before parsing it.
async function readJson<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.toLowerCase().includes('json')) {
        throw new Error(
            `SPO Compute Insights API returned an unexpected response (${response.status}). The API may not be deployed yet.`,
        );
    }

    return response.json() as Promise<T>;
}

async function fetchJson<T>(
    url: string,
    describeFailure: (status: number, body: { error?: string } | null) => string,
    signal?: AbortSignal,
): Promise<T> {
    const response = await apiFetch(url, signal ? { signal } : {});
    if (!response.ok) {
        const body = await response.json().catch(() => null) as { error?: string } | null;
        throw new Error(describeFailure(response.status, body));
    }

    return readJson<T>(response);
}

function fetchCachedJson<T>(
    url: string,
    signal: AbortSignal,
    describeFailure: (status: number, body: { error?: string } | null) => string,
): Promise<T> {
    const now = Date.now();
    let entry = responseCache.get(url);
    if (!entry || entry.expiresAt <= now) {
        // The shared request is not tied to one caller's signal, so another caller can still use the result.
        const created: CacheEntry = { expiresAt: now + RESPONSE_CACHE_TTL_MS, payload: fetchJson<T>(url, describeFailure) };
        created.payload.catch(() => {
            if (responseCache.get(url) === created) {
                responseCache.delete(url);
            }
        });
        responseCache.delete(url);
        responseCache.set(url, created);
        while (responseCache.size > RESPONSE_CACHE_MAX_ENTRIES) {
            responseCache.delete(responseCache.keys().next().value as string);
        }
        entry = created;
    }

    return withAbort(entry.payload as Promise<T>, signal);
}

function withAbort<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
    if (signal.aborted) {
        return Promise.reject(new DOMException('The operation was aborted.', 'AbortError'));
    }

    return new Promise<T>((resolve, reject) => {
        const onAbort = () => reject(new DOMException('The operation was aborted.', 'AbortError'));
        signal.addEventListener('abort', onAbort, { once: true });
        promise.then(
            value => {
                signal.removeEventListener('abort', onAbort);
                resolve(value);
            },
            (error: unknown) => {
                signal.removeEventListener('abort', onAbort);
                reject(error);
            },
        );
    });
}

export function loadComputeFilterOptions(signal: AbortSignal): Promise<ComputeFilterOptions> {
    return fetchCachedJson<ComputeFilterOptions>(
        `${COMPUTE_API_ROOT}/filters`,
        signal,
        status => `Compute filter options request failed (${status}).`,
    );
}

// Source health is diagnostic, so it always asks the server for the current state.
export function loadComputeSourceStatus(signal: AbortSignal): Promise<ComputeSourceStatusResponse> {
    return fetchJson<ComputeSourceStatusResponse>(
        `${COMPUTE_API_ROOT}/source-status`,
        status => `Compute SQL status request failed (${status}).`,
        signal,
    );
}

type BackendPoint = {
    date: string;
    value: number | null;
    secondaryValue?: number | null;
    tertiaryValue?: number | null;
};

type BackendBase = {
    asOfUtc: string;
    dataQualityWarnings?: string[];
};

type ActiveUsersResponse = BackendBase & {
    series: BackendPoint[];
    latestDau: number;
    latestMau: number;
};

type CoresResponse = BackendBase & {
    series: BackendPoint[];
    latestCoresPerKdau: number | null;
    latestUsrCoresPerKdau: number | null;
    latestBotCoresPerKdau: number | null;
};

export async function loadComputeInsight(
    kind: ComputeInsightKind,
    filters: ComputeFilters,
    signal: AbortSignal,
): Promise<ComputeInsightResponse> {
    const query = new URLSearchParams({
        startDate: filters.startDate,
        endDate: filters.endDate,
    });

    if (filters.geo) {
        query.set('geo', filters.geo);
    }
    if (filters.farm) {
        query.set('farmLabel', filters.farm);
    }
    if (filters.customerSegment) {
        query.set('customerSegment', filters.customerSegment);
    }
    if (filters.sku) {
        query.set('sku', filters.sku);
    }

    const endpoint = kind === 'cores-per-kdau-bot' ? 'cores-per-kdau' : kind;
    const payload = await fetchCachedJson<BackendBase>(
        `${COMPUTE_API_ROOT}/${endpoint}?${query}`,
        signal,
        (status, body) => body?.error ?? `SPO Compute Insights request failed (${status}).`,
    );
    return toViewModel(kind, payload, filters);
}

function toViewModel(
    kind: ComputeInsightKind,
    payload: BackendBase,
    filters: ComputeFilters,
): ComputeInsightResponse {
    if (kind === 'active-users') {
        const data = payload as ActiveUsersResponse;
        const rows = aggregateRows(data.series, filters.granularity);
        return response(
            'DAU & MAU',
            data,
            [
                metric('dau', 'Daily active users', data.latestDau),
                metric('mau', 'Monthly active users', data.latestMau),
            ],
            [
                series('dau', 'DAU', rows, point => point.value),
                series('mau', 'MAU', rows, point => point.secondaryValue),
            ],
            rows.map(point => ({
                date: point.date,
                dau: point.value,
                mau: point.secondaryValue ?? null,
            })),
        );
    }

    if (kind === 'cores-per-kdau') {
        const data = payload as CoresResponse;
        const rows = aggregateRows(data.series, filters.granularity);
        return response(
            'Cores/kDAU: Total & USR',
            data,
            [
                metric('total', 'Total cores per kDAU', data.latestCoresPerKdau ?? 'Unavailable'),
                metric('usr', 'USR cores per kDAU', data.latestUsrCoresPerKdau ?? 'Unavailable'),
            ],
            [
                series('total', 'Total', rows, point => point.value),
                series('usr', 'USR', rows, point => point.secondaryValue),
            ],
            rows.map(point => ({
                date: point.date,
                total: point.value,
                usr: point.secondaryValue ?? null,
            })),
        );
    }

    const data = payload as CoresResponse;
    const rows = aggregateRows(data.series, filters.granularity);
    return response(
        'Cores/kDAU: BOT',
        data,
        [
            metric('bot', 'BOT cores per kDAU', data.latestBotCoresPerKdau ?? 'Unavailable'),
        ],
        [
            series('bot', 'BOT', rows, point => point.tertiaryValue),
        ],
        rows.map(point => ({
            date: point.date,
            bot: point.tertiaryValue ?? null,
        })),
    );
}

function response(
    title: string,
    payload: BackendBase,
    summary: ComputeInsightResponse['summary'],
    chartSeries: ComputeSeries[],
    rows: ComputeInsightResponse['table']['rows'],
): ComputeInsightResponse {
    const columns = rows.length === 0
        ? []
        : Object.keys(rows[0]).map(key => ({
            key,
            label: labelFor(key),
            format: key === 'date' ? 'date' as const : 'number' as const,
        }));
    return {
        title,
        freshness: {
            generatedAt: payload.asOfUtc,
            dataThrough: payload.asOfUtc,
            isStale: payload.dataQualityWarnings?.some(warning => warning.toLowerCase().includes('stale')) ?? false,
        },
        warnings: payload.dataQualityWarnings ?? [],
        summary,
        series: chartSeries,
        table: { columns, rows },
    };
}

function metric(key: string, label: string, value: number | string) {
    return { key, label, value, status: 'neutral' as const };
}

function series(
    key: string,
    label: string,
    rows: BackendPoint[],
    select: (point: BackendPoint) => number | null | undefined,
): ComputeSeries {
    return {
        key,
        label,
        points: rows
            .map(point => ({ timestamp: point.date, value: select(point) }))
            .filter((point): point is ComputeSeriesPoint => point.value != null),
    };
}

function aggregateRows(rows: BackendPoint[], granularity: ComputeFilters['granularity']): BackendPoint[] {
    if (granularity === 'day') {
        return rows;
    }

    const grouped = new Map<string, BackendPoint>();
    for (const row of rows) {
        const date = new Date(`${row.date}T00:00:00Z`);
        const key = granularity === 'month'
            ? `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-01`
            : startOfWeek(date);
        const existing = grouped.get(key);
        if (!existing || row.date.localeCompare(existing.date) > 0) {
            grouped.set(key, { ...row });
        }
    }
    return [...grouped.values()].sort((left, right) => left.date.localeCompare(right.date));
}

function startOfWeek(date: Date): string {
    const result = new Date(date);
    const day = result.getUTCDay();
    result.setUTCDate(result.getUTCDate() - ((day + 6) % 7));
    return result.toISOString().slice(0, 10);
}

function labelFor(key: string): string {
    return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, value => value.toUpperCase());
}
